import type { FeedbackEvent } from '../sim/feedback';
import type { MotionState } from '../sim/motion';

/**
 * Placeholder synth SFX — a feedback-bus subscriber (design: audio/VFX/haptics all subscribe to
 * the same events; adding real samples later means swapping this file, nothing upstream).
 * Everything is synthesized in WebAudio so the sandbox ships zero audio assets. The context is
 * created lazily on the first user gesture (browser autoplay policy).
 */

/** Per-surface step/land character: filter cutoff (Hz), duration scale, optional water splash. */
const SURFACE_SOUND: Record<string, { cutoff: number; durMul: number; splash?: boolean }> = {
  default: { cutoff: 900, durMul: 1 },
  metal: { cutoff: 1800, durMul: 0.7 },
  grass: { cutoff: 500, durMul: 1.1 },
  wood: { cutoff: 900, durMul: 0.9 },
  sand: { cutoff: 420, durMul: 1.6 },
  ice: { cutoff: 2400, durMul: 1.2 },
  water: { cutoff: 480, durMul: 1.5, splash: true },
};

const surfaceSound = (tag: string) => SURFACE_SOUND[tag] ?? SURFACE_SOUND['default']!;

export class Sfx {
  enabled = true;
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private hoverOsc: OscillatorNode | null = null;
  private hoverGain: GainNode | null = null;
  private strideMeters = 0;

  /** Call once from a user-gesture handler (click/keydown) to unlock audio. */
  unlock(): void {
    if (this.ctx) {
      void this.ctx.resume();
      return;
    }
    this.ctx = new AudioContext();
    this.master = this.ctx.createGain();
    this.master.gain.value = 0.35;
    this.master.connect(this.ctx.destination);
  }

  handle(events: FeedbackEvent[]): void {
    if (!this.enabled || !this.ctx || !this.master) return;
    for (const e of events) {
      switch (e.type) {
        case 'jump':
          this.chirp(280, 560, 0.1, 0.18);
          break;
        case 'land': {
          const s = surfaceSound(e.surface);
          this.thud(0.12 + 0.5 * e.intensity, (s.cutoff / 900) * (400 + 600 * e.intensity), 0.14 * s.durMul);
          if (s.splash) this.noiseBurst(0.1 + 0.2 * e.intensity, 700, 0.3, 'lowpass');
          break;
        }
        case 'skid':
          this.noiseBurst(0.18, 1800, 0.12);
          break;
        case 'roll':
          this.noiseBurst(0.22, 1000, 0.28, 'lowpass');
          break;
        case 'teeter':
          this.chirp(220, 180, 0.09, 0.08);
          break;
        case 'hoverStart':
          this.startHoverLoop();
          this.chirp(160, 340, 0.08, 0.15);
          break;
        case 'hoverWarn':
          this.sputter();
          break;
        case 'hoverEnd':
          this.stopHoverLoop();
          if (e.intensity > 0) this.chirp(300, 140, 0.12, 0.1);
          break;
        case 'break':
          // Ceramic crack: sharp high snap over a low body thud (entity-emitted, E0).
          this.noiseBurst(0.3, 2600, 0.07);
          this.thud(0.22, 420, 0.12);
          break;
      }
    }
  }

  /** Per-frame: hover pitch follows vertical velocity; footsteps by stride distance. */
  update(state: MotionState, dt: number): void {
    if (!this.ctx) return;
    if (this.hoverOsc) {
      this.hoverOsc.frequency.value = 110 + state.vel.y * 18;
    }
    const speed = Math.hypot(state.vel.x, state.vel.z);
    if (state.grounded && speed > 1.5) {
      this.strideMeters += speed * dt;
      const stride = 1.9; // meters per footstep at run
      if (this.strideMeters >= stride) {
        this.strideMeters = 0;
        const s = surfaceSound(state.groundSurface);
        if (this.enabled) {
          this.thud(0.05, s.cutoff, 0.04 * s.durMul);
          if (s.splash) this.noiseBurst(0.04, 650, 0.09, 'lowpass');
        }
      }
    } else {
      this.strideMeters = 0;
    }
  }

  private chirp(from: number, to: number, dur: number, gain: number): void {
    if (!this.ctx || !this.master) return;
    const t0 = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(from, t0);
    osc.frequency.exponentialRampToValueAtTime(Math.max(to, 1), t0 + dur);
    g.gain.setValueAtTime(gain, t0);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
    osc.connect(g).connect(this.master);
    osc.start(t0);
    osc.stop(t0 + dur + 0.02);
  }

  private thud(gain: number, cutoff: number, dur = 0.14): void {
    this.noiseBurst(gain, cutoff, dur, 'lowpass');
  }

  private noiseBurst(
    gain: number,
    cutoff: number,
    dur: number,
    type: BiquadFilterType = 'highpass',
  ): void {
    if (!this.ctx || !this.master) return;
    const t0 = this.ctx.currentTime;
    const len = Math.ceil(this.ctx.sampleRate * dur);
    const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / len);
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const filter = this.ctx.createBiquadFilter();
    filter.type = type;
    filter.frequency.value = cutoff;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(gain, t0);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
    src.connect(filter).connect(g).connect(this.master);
    src.start(t0);
  }

  private startHoverLoop(): void {
    if (!this.ctx || !this.master || this.hoverOsc) return;
    this.hoverOsc = this.ctx.createOscillator();
    this.hoverOsc.type = 'sawtooth';
    this.hoverOsc.frequency.value = 120;
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 500;
    this.hoverGain = this.ctx.createGain();
    this.hoverGain.gain.value = this.enabled ? 0.07 : 0;
    this.hoverOsc.connect(filter).connect(this.hoverGain).connect(this.master);
    this.hoverOsc.start();
  }

  private sputter(): void {
    if (!this.ctx || !this.hoverOsc || !this.hoverGain) return;
    const t0 = this.ctx.currentTime;
    // Telegraph the cutoff: pitch drops and the loop stutters for the warn window.
    this.hoverOsc.frequency.setValueAtTime(this.hoverOsc.frequency.value, t0);
    this.hoverOsc.frequency.linearRampToValueAtTime(60, t0 + 0.35);
    for (let i = 0; i < 4; i++) {
      this.hoverGain.gain.setValueAtTime(0.02, t0 + i * 0.09);
      this.hoverGain.gain.setValueAtTime(this.enabled ? 0.07 : 0, t0 + i * 0.09 + 0.045);
    }
  }

  private stopHoverLoop(): void {
    if (this.hoverOsc) {
      this.hoverOsc.stop();
      this.hoverOsc.disconnect();
      this.hoverOsc = null;
    }
    if (this.hoverGain) {
      this.hoverGain.disconnect();
      this.hoverGain = null;
    }
  }

  dispose(): void {
    this.stopHoverLoop();
    void this.ctx?.close();
    this.ctx = null;
  }
}
